import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { IconButton, Tooltip } from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { usePutLicensesMutation } from "Api/Mutations/Licenses/usePutLicenses";
import { getPartyQueryKey } from "Api/Queries/Party/usePartyQuery";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { LicenseFormFields } from "Components/Users/UserDetailPageContent/Components/LicenseForm/LicenseFormFields";
import { SendInvoiceButton } from "Components/Users/UserDetailPageContent/Components/LicenseForm/SendInvoiceButton";
import { RegenerateInvoiceButton } from "Components/Users/UserDetailPageContent/Components/LicenseForm/RegenerateInvoiceButton";
import { Stack } from "@mui/material";
import { TransferLicenseDialog } from "Components/Users/UserDetailPageContent/Components/LicenseForm/TransferLicenseDialog";
import {
  type LicenseFormData,
  schema,
} from "Components/Users/UserDetailPageContent/Components/LicenseForm/schema";
import { StyledForm } from "Components/Users/UserDetailPageContent/Components/StyledForm";
import { useCheckPermission } from "Hooks/usePermission";
import {
  LicensePaymentStatus,
  type PartyLicenseDto,
  Permission,
} from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Licenses.Form.Controls;
const TRANSFER_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Licenses.Transfer;

type LicenseFormProps = {
  license: PartyLicenseDto;
  partyPublicID: string;
};

export const LicenseForm = ({ license, partyPublicID }: LicenseFormProps) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const canTransferLicense = useCheckPermission(
    AccessRightCodes.Licenses,
    Permission.Update,
  );
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<LicenseFormData>({
    resolver: zodResolver(schema),
    defaultValues: license,
  });

  const paymentStatus = watch("paymentStatus");
  const paymentDate = watch("paymentDate");

  const isTransferAvailable =
    canTransferLicense &&
    license.paymentStatus === LicensePaymentStatus.Success;

  const { mutate: updateLicensePaymentStatus, isPending } =
    usePutLicensesMutation({
      onSuccess: (data) => {
        reset({ ...license, ...data.partyLicenseDto });
        queryClient.invalidateQueries({
          queryKey: getPartyQueryKey(partyPublicID),
        });
        toast.success(t(COMPONENT_RESOURCES.SubmitSuccess));
      },
      onError: () => toast.error(t(COMPONENT_RESOURCES.SubmitError)),
    });

  const onSubmit = (data: LicenseFormData) => {
    updateLicensePaymentStatus({
      publicID: license.publicID,
      requestPayload: data,
    });
  };

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <PrimaryPaper>
        <LicenseFormFields
          control={control}
          isLicenseNoLocked={!!license.licenseNo}
          transferButtonSlot={
            isTransferAvailable && (
              <Tooltip title={t(TRANSFER_RESOURCES.Button)}>
                <IconButton
                  aria-label={t(TRANSFER_RESOURCES.Button)}
                  onClick={() => setIsTransferDialogOpen(true)}
                  sx={{ mt: "25px" }}
                >
                  <SwapHorizIcon />
                </IconButton>
              </Tooltip>
            )
          }
          sendInvoiceButtonSlot={
            <Stack direction="row" spacing={1}>
              <SendInvoiceButton
                licensePublicID={license.publicID}
                partyPublicID={partyPublicID}
                invoiceEmailSent={license.invoiceEmailSent}
                paymentDate={paymentDate}
                paymentStatus={paymentStatus}
                isDirty={isDirty}
              />
              <RegenerateInvoiceButton
                licensePublicID={license.publicID}
                invoiceEmailSent={license.invoiceEmailSent}
              />
            </Stack>
          }
        />
        <PrimaryButton
          fullWidth
          type="submit"
          disabled={!isDirty}
          isLoading={isPending}
          variant="contained"
          color="primary"
        >
          {t(COMPONENT_RESOURCES.Save)}
        </PrimaryButton>
      </PrimaryPaper>
      <TransferLicenseDialog
        license={license}
        partyPublicID={partyPublicID}
        isOpen={isTransferDialogOpen}
        onClose={() => setIsTransferDialogOpen(false)}
      />
    </StyledForm>
  );
};
